/**
 * Placing a block of point text on a plane in the world.
 *
 * The plane is projected by sampling three world points — the origin and one em
 * along each local axis — through the SAME `worldToCamera` the fragment field
 * uses, then taking screen-space differences. That gives real foreshortening
 * from the real view transform, rather than a second, subtly different
 * projection that would drift out of agreement with everything else on screen.
 *
 * ── Billboarding, and why it is partial ───────────────────────────────────
 * Fully billboarded text always faces the camera and reads as a sticker pasted
 * on the lens. Fully world-locked text is unreadable from most of the room. So
 * the plane's forward direction is a blend: mostly its own, leaning slightly
 * toward the viewer. `BILLBOARD` is that lean, and the useful range is narrow.
 *
 * ── The two fades that stop it looking broken ─────────────────────────────
 * Grazing: past ~70° off the plane normal, letters collapse into a line and read
 * as a smear. Distance: point text dissolves before it becomes small. Both fade
 * out rather than clip, because a hard cutoff pops — and fragments cluster, so
 * several pop at once.
 */

import type { CameraPose, Vec3 } from '../../spatial/SpatialBackend.js';
import type { ViewGeometry } from '../../spatial/cameraFov.js';
import { worldToCamera } from '../../field/placement.js';

/** How much the plane turns toward the viewer. 0 = world-locked, 1 = billboard. */
export const BILLBOARD = 0.22;

/** Cosine of the angle past which the plane is too edge-on to read. */
const GRAZE_FADE_START = 0.35; // ~70 deg
const GRAZE_FADE_END = 0.1; // ~84 deg

/**
 * Distance fade, as MULTIPLES of how far away the text was placed.
 *
 * ⚠️ These were absolute SLAM units (8 and 14) and it was the single worst bug
 * in the rendering path. **A SLAM unit is arbitrary and differs every session**,
 * so an absolute fade distance is a lottery ticket drawn fresh each run — the
 * same mistake `SceneDepth` was written to remove from placement, left in place
 * for visibility.
 *
 * The capture of 2026-08-25 shows both ways it loses, in one session:
 *
 *  - The unit was roughly 7 cm. Fragments that fell to the rung-3 constant
 *    (3.2 units) were planted **~22 cm from the participant's face** — huge in
 *    angular terms, so the glyph point cloud stretched into unreadable crumbs —
 *    and a single 0.7 m stride is 10 units, which crossed the entire 10.8-unit
 *    visibility window. Text vanished on one step.
 *  - Fragments placed by the rung-2 path that actually WORKS landed at 0.8x a
 *    scene depth of tens of units, i.e. **past FAR_FADE_END before they were
 *    ever drawn**. They were invisible from birth.
 *
 * So the better the depth estimate became, the more invisible the text got.
 *
 * Expressed as ratios, the window travels with the placement: 2.5x and 4.5x
 * reproduce the old 8/14 exactly when text is placed at 3.2 units, and stay
 * sensible when a unit is a centimetre or a metre.
 */
const FAR_FADE_START_RATIO = 2.5;
const FAR_FADE_END_RATIO = 4.5;
/** Near fade, likewise relative — 0.35/3.2 of the old absolute pair. */
const NEAR_FADE_RATIO = 0.11;

export interface PlacedText {
  /** Block origin in CSS px. */
  ox: number;
  oy: number;
  /** Screen vector for one em of local +x / +y, in CSS px. */
  bx: [number, number];
  by: [number, number];
  /** Combined grazing, distance and edge fade. */
  alpha: number;
  visible: boolean;
  distance: number;
}

const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const mul = (a: Vec3, s: number): Vec3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });
const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const cross = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});
function norm(v: Vec3): Vec3 {
  const l = Math.hypot(v.x, v.y, v.z) || 1;
  return { x: v.x / l, y: v.y / l, z: v.z / l };
}

/**
 * Build the plane basis for a text block.
 *
 * @param facing The plane's own outward normal — a surface normal when the
 *   backend supplies one, otherwise the direction the camera faced when the
 *   fragment was anchored. Frozen at anchor time: recomputing it per frame is
 *   billboarding by accident.
 */
export function textPlaneBasis(facing: Vec3, cam: CameraPose): { right: Vec3; up: Vec3 } {
  const toCam = norm(sub(cam.position, { x: 0, y: 0, z: 0 }));
  // Lean the normal toward the viewer by BILLBOARD, then re-normalise.
  const n = norm(add(mul(norm(facing), 1 - BILLBOARD), mul(toCam, BILLBOARD)));

  const worldUp: Vec3 = { x: 0, y: 1, z: 0 };
  // Degenerate when the plane is horizontal (text lying on a floor or ceiling):
  // fall back to an arbitrary but stable in-plane axis.
  let right = cross(worldUp, n);
  if (Math.hypot(right.x, right.y, right.z) < 1e-4) {
    right = cross({ x: 0, y: 0, z: 1 }, n);
  }
  right = norm(right);
  const up = norm(cross(n, right));
  return { right, up };
}

/**
 * @param refDistance How far away this text was PLACED, in world units — the
 *   anchor's `placementDistance`. The distance fades are computed as multiples
 *   of it, so they mean the same thing whatever a unit happens to be worth this
 *   session. Passing the live camera distance here instead would make the fade
 *   self-referential and it would never trigger.
 */
export function placeText(
  world: Vec3,
  right: Vec3,
  up: Vec3,
  emWorld: number,
  cam: CameraPose,
  view: ViewGeometry,
  viewW: number,
  viewH: number,
  refDistance: number,
): PlacedText {
  const toScreen = (p: Vec3) => {
    const c = worldToCamera(p, cam, view);
    return {
      x: (0.5 + (c.nx * 0.5)) * viewW,
      y: (0.5 - (c.ny * 0.5)) * viewH,
      inFront: c.inFront,
      distance: c.distance,
    };
  };

  const o = toScreen(world);
  if (!o.inFront) {
    return {
      ox: 0, oy: 0, bx: [0, 0], by: [0, 0],
      alpha: 0, visible: false, distance: o.distance,
    };
  }
  const px = toScreen(add(world, mul(right, emWorld)));
  const py = toScreen(add(world, mul(up, emWorld)));

  const bx: [number, number] = [px.x - o.x, px.y - o.y];
  const by: [number, number] = [py.x - o.x, py.y - o.y];

  // Grazing: how square-on the plane is to the view direction.
  const n = norm(cross(right, up));
  const viewDir = norm(sub(world, cam.position));
  const facingness = Math.abs(dot(n, viewDir));
  const graze = smoothstep(GRAZE_FADE_END, GRAZE_FADE_START, facingness);

  // Distance, against the anchor's own placement distance rather than an
  // absolute unit count — see the FAR_FADE_*_RATIO note.
  const d = o.distance;
  const ref = refDistance > 1e-6 ? refDistance : d;
  const far = 1 - smoothstep(ref * FAR_FADE_START_RATIO, ref * FAR_FADE_END_RATIO, d);
  const near = smoothstep(0, ref * NEAR_FADE_RATIO, d);

  // Off-frame dissolve, matching the field's own edge behaviour.
  const overX = Math.max(0, Math.abs(o.x / viewW - 0.5) * 2 - 1);
  const overY = Math.max(0, Math.abs(o.y / viewH - 0.5) * 2 - 1);
  const edge = Math.max(0, 1 - Math.max(overX, overY) / 0.35);

  const alpha = graze * far * near * edge;
  const emPx = Math.hypot(bx[0], bx[1]);

  return {
    ox: o.x,
    oy: o.y,
    bx,
    by,
    alpha,
    visible: alpha > 0.004 && emPx > 2,
    distance: d,
  };
}

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}
